import { ApiError, errorEnvelopeSchema } from './types'

export interface ApiClientConfig {
  baseUrl: string
  getToken: () => Promise<string>
}

export interface ApiClient {
  get: <T>(path: string, params?: Record<string, string>) => Promise<T>
  rpc: <T>(
    name: string,
    body: Record<string, unknown>,
    options?: { signal?: AbortSignal },
  ) => Promise<T>
  post: <T>(
    path: string,
    body: unknown,
    options?: { headers?: Record<string, string> },
  ) => Promise<T>
  patch: <T>(
    path: string,
    body: unknown,
    options?: { headers?: Record<string, string> },
  ) => Promise<T>
  delete: (path: string, params?: Record<string, string>) => Promise<void>
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  async function request<T>(method: string, path: string, init: RequestInit = {}): Promise<T> {
    const token = await config.getToken()
    const url = `${config.baseUrl}${path}`
    const res = await fetch(url, {
      method,
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(init.headers ?? {}),
      },
    })

    const contentType = res.headers.get('content-type') ?? ''
    const isJson = contentType.includes('application/json')

    if (!res.ok) {
      const body = isJson ? await res.json().catch(() => null) : null
      const parsed = body ? errorEnvelopeSchema.safeParse(body) : null
      const envelope = parsed?.success
        ? parsed.data
        : { code: `HTTP-${res.status}`, message: res.statusText || 'request failed' }
      throw new ApiError(envelope, res.status, url)
    }

    if (!isJson) return undefined as T
    return (await res.json()) as T
  }

  return {
    get: (path, params) => {
      const qs = params ? `?${new URLSearchParams(params).toString()}` : ''
      return request('GET', `${path}${qs}`)
    },
    rpc: (name, body, options) =>
      request('POST', `/rpc/${name}`, { body: JSON.stringify(body), signal: options?.signal }),
    post: (path, body, options) =>
      request('POST', path, { body: JSON.stringify(body), headers: options?.headers }),
    patch: (path, body, options) =>
      request('PATCH', path, { body: JSON.stringify(body), headers: options?.headers }),
    delete: (path, params) => {
      const qs = params ? `?${new URLSearchParams(params).toString()}` : ''
      return request('DELETE', `${path}${qs}`)
    },
  }
}
